import type { DeploymentRequestResourceReadV2Api } from '@ingenico/api-sdk-device-management-private-v2';
import { toArray, toString } from 'lodash-es';
import type {
  DeploymentRequestResource,
  DeploymentRequestResourceOptionConfiguration,
  DeploymentRequestResourceOptionDelete,
  DeploymentRequestResourceReference,
} from '../../models/deployment/deploymentById.model';
import { toIsoStringIfDate } from '../utils/date.utils';

function manageUpdateDeploymentRequestByIdDynamicGroupReference(
  reference: NonNullable<DeploymentRequestResourceReadV2Api['dynamicGroup']['reference']>,
): DeploymentRequestResourceReference {
  return {
    description: '',
    name: '',
    id: reference?.id,
  };
}

function getDynamicGroupReference(
  reference: DeploymentRequestResourceReadV2Api['dynamicGroup']['reference'],
): DeploymentRequestResourceReference | undefined {
  return reference === undefined ? undefined : manageUpdateDeploymentRequestByIdDynamicGroupReference(reference);
}

function manageOptionsDelete(
  optionsDelete: DeploymentRequestResourceReadV2Api['options']['delete'],
): DeploymentRequestResourceOptionDelete {
  return {
    ...optionsDelete,
    filePaths: [],
    packageIds: [],
    systemPackageVersionsIds: [],
    applicationVersionsIds: toArray(
      optionsDelete?.applicationVersionsIds.map((applicationVersionId) => ({
        applicationId: '',
        applicationName: '',
        applicationLogo: {},
        applicationVersionId,
      })),
    ),
  };
}

function manageOptionsConfiguration(
  optionsConfiguration: NonNullable<DeploymentRequestResourceReadV2Api['options']['configuration']>,
): DeploymentRequestResourceOptionConfiguration {
  return {
    ...optionsConfiguration,
    reference: getDynamicGroupReference(optionsConfiguration?.reference),
    snapshotId: optionsConfiguration?.snapshotId,
    type: optionsConfiguration?.type,
    custom: {
      ...optionsConfiguration?.custom,
      definition: {
        ...optionsConfiguration?.custom?.definition,
        configuration: {
          ...optionsConfiguration?.custom?.definition?.configuration,
          applicationVersions: toArray(
            optionsConfiguration?.custom?.definition?.configuration?.applicationVersions?.map(
              ({ id, order, configurationModuleName, type }) => ({
                applicationId: '',
                applicationLogo: {},
                applicationName: '',
                applicationStatus: 'PUBLISHED',
                applicationVersionId: '',
                applicationVersionNumber: '',
                applicationVersionStatus: 'PUBLISHED',
                id,
                order,
                type,
                configurationModuleName: toString(configurationModuleName),
                applicationVersions: [],
              }),
            ),
          ),
          osVersion: {
            ...optionsConfiguration?.custom?.definition?.configuration?.osVersion,
            id: toString(optionsConfiguration?.custom?.definition?.configuration?.osVersion?.id),
            osId: '',
            osLogo: {},
            osName: '',
            osStatus: 'PUBLISHED',
            osVersionId: toString(optionsConfiguration?.custom?.definition?.configuration?.osVersion?.id),
            osVersionNumber: '',
            osVersions: [],
            osVersionStatus: 'PUBLISHED',
          },
          systemPackageVersions: toArray(
            optionsConfiguration?.custom?.definition?.configuration?.systemPackageVersions?.map(
              ({ id, order, type, configurationModuleName }) => ({
                id,
                order,
                systemPackageId: '',
                systemPackageLogo: {},
                systemPackageName: '',
                systemPackageStatus: 'PUBLISHED',
                systemPackageVersionId: '',
                systemPackageVersionNumber: '',
                systemPackageVersions: [],
                systemPackageVersionStatus: 'PUBLISHED',
                type,
                configurationModuleName,
              }),
            ),
          ),
        },
        compositions: toArray(
          optionsConfiguration?.custom?.definition?.compositions?.map(({ configurationId }) => ({ configurationId })),
        ),
      },
      creationDate: toIsoStringIfDate(optionsConfiguration?.custom?.creationDate),
      creator: {
        id: toString(optionsConfiguration?.custom?.creator?.id),
        // name: toString(optionsConfiguration?.custom?.creator?.name ?? ''),
        name: toString(''), //Impacted changes due ticket MDMGT-7043
      },
      id: toString(optionsConfiguration?.custom?.id),
      updateDate: toIsoStringIfDate(optionsConfiguration?.custom?.updateDate),
      updatedBy: {
        id: toString(optionsConfiguration?.custom?.updatedBy?.id),
        name: '',
      },
    },
  };
}

function mapTrigger(
  trigger: NonNullable<DeploymentRequestResourceReadV2Api['trigger']>,
): DeploymentRequestResource['trigger'] {
  const mappedScheduleOption = trigger?.scheduleOption
    ? {
        date: toIsoStringIfDate(trigger.scheduleOption.date),
        daysOfWeek: trigger.scheduleOption.daysOfWeek ?? [],
        exclusionDates: trigger.scheduleOption.exclusionDates?.map((date) => toIsoStringIfDate(date)),
        maxNoOfDeployments: trigger.scheduleOption.maxNoOfDeployments,
        timeSlot: trigger.scheduleOption.timeSlot
          ? {
              startDate: toIsoStringIfDate(trigger.scheduleOption.timeSlot.startDate),
              endDate: toIsoStringIfDate(trigger.scheduleOption.timeSlot.endDate),
            }
          : undefined,
      }
    : undefined;

  return {
    type: trigger.type as DeploymentRequestResource['trigger']['type'],
    scheduleOption: mappedScheduleOption,
  };
}

export function mapUpdateDeploymentRequestByIdResponse(response: DeploymentRequestResourceReadV2Api) {
  return {
    description: response.description,
    name: response.name,
    id: response.id,
    status: response.status,
    statusDetail: response.statusDetail,
    creator: response.creator as DeploymentRequestResource['creator'],
    dynamicGroup: {
      deviceCount: response.dynamicGroup.deviceCount,
      query: response.dynamicGroup.query,
      snapshotId: toString(response.dynamicGroup.snapshotId),
      reference: getDynamicGroupReference(response.dynamicGroup.reference),
      type: response.dynamicGroup.type,
    } as DeploymentRequestResource['dynamicGroup'],
    creationDate: toIsoStringIfDate(response.creationDate),
    endDate: toIsoStringIfDate(response.endDate),
    startDate: toIsoStringIfDate(response.startDate),
    trigger: mapTrigger(response.trigger as NonNullable<DeploymentRequestResourceReadV2Api['trigger']>),
    options: {
      delete: manageOptionsDelete(response.options.delete),
      configuration: response.options.configuration
        ? manageOptionsConfiguration(response.options.configuration)
        : undefined,
    } as DeploymentRequestResource['options'],
  } as DeploymentRequestResource;
}
